import { default as qs } from 'query-string';

import { decamelizeDeep } from '@vpr/restful';

import {
  buildAcceptHeader,
  ResourceDeclaration,
  standardizeResponseObject,
} from '@vimeo/core/hooks/use-api-resource/libs';
import { AnyObject, Maybe } from '@vimeo/core/types/generic';

import { GctlConfig } from './dreamstate_gctl_scope';
import { liveApplicationConfig } from '../../configs/LiveApplicationConfig';
import { ERequestMethod } from '../../types/api';
import { ELiveErrorCode } from '../../types/error';
import { LiveError } from '../../utils/error';

/**
 * Params describing API call.
 * Everything required to do proper fetch from vimeo backend.
 */
export interface IApiCallParameters {
  /**
   * Request path relative to API resource.
   *
   * Example:
   *  '/sample/path'
   */
  path: string;
  /**
   * Vimeo API GCTL configuration (host, jwt, language).
   */
  gctlConfig: GctlConfig;
  /**
   * Query object to include some data in URL params.
   * Will be automatically encoded and transformed.
   */
  query?: AnyObject;
  /**
   * Fields to be fetched from backend resource, by default - all available data.
   * Try to pick only needed resources to reduce load times and server load.
   */
  fields?: ReadonlyArray<string>;
  /**
   * Request method.
   */
  method?: ERequestMethod;
  /**
   * Request body.
   */
  body?: AnyObject;
  /**
   * Whether response should be standardized (camelize, unpack, format).
   */
  standardize?: boolean;
  /**
   * Whether error should be checked in API response automatically and thrown as LiveError.
   */
  isErrorAssertNeeded?: boolean;
  /**
   * Whether legacy request should be performed and use current domain as base instead of api.vimeo*.
   */
  isLegacyRequest?: boolean;
}

/**
 * Options to control API request flow.
 */
export interface IApiCallOptions {
  /**
   * Whether URL path query should be encoding some meta characters.
   */
  encodeQuery?: boolean;
  /**
   * Additional headers to provide with API request.
   */
  additionalHeaders?: AnyObject;
}

/**
 * Generic type used with api methods, allows skipping repeated fields declaration.
 */
export type TApiMethodParameters<T> = T & {
  gctlConfig: GctlConfig;
  fields?: Array<string>;
  standardize?: boolean;
};

/**
 * Check response format and rethrow exception if got response but it still indicates fetch error.
 *
 * @param response - response object containing raw information (code, headers, statuses etc).
 * @param body - optional body data from response.
 * @param code - error code for usage in reporting.
 */
export function assertNoResponseError(
  response: Response,
  body: Maybe<AnyObject>,
  code: ELiveErrorCode = ELiveErrorCode.API_ERROR,
): void | never {
  const isErrorStatus: boolean = response.status >= 400;
  const isErrorResponse = body && typeof body === 'object' && 'error' in body;

  /**
   * If got error format response from vimeo or bad status code.
   */
  if (isErrorResponse || isErrorStatus) {
    const errorMessage: string = isErrorResponse
      ? body.developerMessage || body.error
      : response.statusText + '.';
    const errorReport: string = `${ELiveErrorCode[code]} (${
      body?.errorCode || response.status
    }): ${errorMessage}`;

    throw new LiveError(errorReport, {
      code,
      data: {
        url: response.url,
        status: response.status,
        ok: response.ok,
        body: body,
        contentType: response.headers.get('Content-Type'),
      },
    });
  }
}

/**
 * Get URL for api call.
 * In case of normal API request do complete URL generation with new subdomain.
 * In case of legacy request just use URI and use current domain.
 *
 * @param config - configuration of endpoint api URL
 * @param config.isLegacyRequest - whether performing legacy request
 * @param config.path - relative path of endpoint
 * @param config.gctlConfig - current GCTL config object
 */
export function getEndpointApiURL({
  isLegacyRequest,
  path,
  gctlConfig,
}: IApiCallParameters): string {
  return isLegacyRequest ? path : `${gctlConfig?.baseUrl}${path}`;
}

/**
 * Returns accept header for live applications.
 */
export function getApplicationAcceptHeader(
  resource?: ResourceDeclaration | Array<ResourceDeclaration>,
): string {
  return `${buildAcceptHeader(resource)}, application/json, text/plain, text/csv`;
}

/**
 * Check if response content can be parsed as json.
 * Valid cases:
 *  - JSON content type
 *  - one of Vimeo defined custom types (application/vnd.vimeo.*)
 *
 * @param contentType - response object from ajax/fetch call
 */
export function isJsonResponse(contentType: Maybe<string>): boolean {
  const responseContentType: Maybe<string> = String(contentType).toLowerCase();
  const isJson: boolean = responseContentType === 'application/json';
  const isVndVimeo: boolean = responseContentType.startsWith('application/vnd.vimeo');

  return isJson || isVndVimeo;
}

/**
 * Check if response content can be considered blob.
 *
 * @param contentType - response object from ajax/fetch call
 */
export function isBlobResponse(contentType: Maybe<string>): boolean {
  const expectedBlobs: Array<string> = ['text/plain', 'text/csv'];
  const responseContentType: Maybe<string> = String(contentType).toLowerCase();

  return expectedBlobs.some((it) => responseContentType.includes(it));
}

/**
 * Send generic API request to vimeo backend and do set of pre-defined transformations and preparations.
 *
 * @param params - params generally used for all API requests.
 * @param options - options to control request flow/modify it.
 * @returns response - async request response.
 */
export async function sendApiRequest<T>(
  params: IApiCallParameters,
  options: IApiCallOptions = {},
): Promise<T> {
  const {
    query,
    body,
    method = ERequestMethod.GET,
    fields = [],
    standardize = true,
    isErrorAssertNeeded = true,
  } = params;

  // todo: Support of fields for connection based requests.

  /**
   * Prepare query based on provided params and fields.
   */
  const targetQuery: string = qs.stringify(
    {
      ...query,
      fields: fields.length ? fields.join(',') : undefined,
    },
    { encode: options.encodeQuery },
  );

  /**
   * If input is already with query, just add more fields in the end.
   */
  const input: string = getEndpointApiURL(params);
  const querySeparator: string = input.indexOf('?') === -1 ? '?' : '&';
  const url: string = `${input}${targetQuery ? `${querySeparator}${targetQuery}` : ''}`;
  const bodyRaw: Maybe<BodyInit> = body ? JSON.stringify(decamelizeDeep(body)) : undefined;
  const headers: Headers = generateAPIHeaders(params, options.additionalHeaders);

  /**
   * In better world we should not send request if we are sure it will fail.
   * Adding disposed / expired checks before sending request to track problems with jwt refresh.
   */
  const errorCode: ELiveErrorCode = liveApplicationConfig.IS_ACTIVE
    ? ELiveErrorCode.API_ERROR
    : ELiveErrorCode.DISPOSED;

  const response: Response = await fetch(url, {
    body: bodyRaw,
    method: method,
    headers: headers,
  });

  /**
   * In some cases there is no response body, so we cannot transform/check/camelize it.
   * In some cases we get content type different from JSON.
   *
   * Examples:
   *  - Error responses with HTML inside
   *  - Response::ok
   *  - Response::deleted
   */

  const responseContentType: Maybe<string> = response.headers.get('Content-Type');
  const isJsonExpected: boolean = isJsonResponse(responseContentType);
  const json: T = isJsonExpected ? await response.json() : null;
  const responseBody: T = json && standardize ? (standardizeResponseObject(json) as T) : json;

  /**
   * Assert response validity, error state and status code.
   */
  isErrorAssertNeeded
    ? assertNoResponseError(response, responseBody as Maybe<AnyObject>, errorCode)
    : void 0;

  if (isBlobResponse(responseContentType)) {
    return (await response.blob()) as unknown as T;
  } else {
    return responseBody;
  }
}

/**
 * Generate default headers required for vimeo API.
 *
 * @param params - params generally used for all API requests.
 * @param additional - additional headers for overriding.
 * @returns headers - formatted request headers.
 */
export function generateAPIHeaders(
  params: IApiCallParameters,
  additional: AnyObject = {},
): Headers {
  const headers: Headers = new Headers({
    Accept: getApplicationAcceptHeader(),
    Authorization: `jwt ${params.gctlConfig?.jwt}`,
    'Content-Type': 'application/json',
    'Vimeo-Page': params.path,
    ...(params.gctlConfig?.locale ? { 'Accept-Language': String(params.gctlConfig.locale) } : {}),
    ...additional,
  });

  if (params.isLegacyRequest) {
    headers.set('X-Requested-With', 'XMLHttpRequest');
  }

  return headers;
}
